import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useDispatch, useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import moment from 'moment'

import DropDown from '../../../../components/Input/DropDown'
import Checkbox from '../../../../components/Input/Checkbox'
import UploadAvatar from '../../../../components/Upload/UploadAvatar'
import ProfilePublicView from './ProfilePublicView'
import Button from 'components/UI/Button'
import ErrorMessageWithExclamation from '../../../../components/UI/ErrorMessageWithExclamation'
import Input from '../../../../components/UI/Input'

import {
  selectIsLoading,
  selectRole,
  selectUserData,
} from '../../../../routing/fetchingfunction/user/user.reducer'
import {
  setStep,
  updateAbout,
  updateRegistraionAbout,
} from '../../../../routing/fetchingfunction/user/user.action'

import { ProfileStudentScheme, ProfileTutorScheme } from '../../../../routing/utils/validators'

import { getAvatarUrl, getProfileByRole } from '../../../../helpers'

import { formattedCountryCodes, genders, TUTOR_ROLE } from '../../../../constants'

const Profile = () => {
  const dispatch = useDispatch()

  const userData = useSelector(selectUserData)
  const role = useSelector(selectRole)
  const isLoading = useSelector(selectIsLoading)

  const isTutor = role === TUTOR_ROLE
  const profile = getProfileByRole(userData, role)

  const {
    formState: { errors },
    handleSubmit,
    reset,
    register,
    control,
    watch,
    resetField,
  } = useForm({
    defaultValues: {
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      username: profile?.username || '',
      gender: userData?.profile?.gender || '',
      birth_date: moment(userData?.profile?.birth_date || ''),
      phone_number_part: userData?.profile?.phone_number_part || '',
      country_code: userData?.profile?.country_code || '',
    },
    resolver: yupResolver(isTutor ? ProfileTutorScheme : ProfileStudentScheme),
  })

  useEffect(() => {
    if (profile || userData) {
      reset({
        first_name: profile?.first_name || '',
        last_name: profile?.last_name || '',
        username: profile?.username || '',
        gender: userData?.profile?.gender || '',
        birth_date: moment(userData?.profile?.birth_date || ''),
        phone_number_part: userData?.profile?.phone_number_part || '',
        country_code: userData?.profile?.country_code || '',
      })
    }
  }, [profile, userData, reset])

  const [isPublicViewOpen, setIsPublicViewOpen] = useState(false)

  const profile_picture = watch('profile_picture')
  const gender = watch('gender')

  // const handleFormSubmit = (data) => {
  //   dispatch(
  //     updateAbout({
  //       id: userData.id || userData.pk,
  //       profile: {
  //         ...userData.profile,
  //         ...data,
  //         birth_date: data.birth_date
  //           ? moment(data.birth_date).format('YYYY-MM-DD')
  //           : userData.profile.birth_date,
  //         profile_picture: profile_picture ? profile_picture[0] : undefined,
  //       },
  //     })
  //   ).then((data) => {
  //     if (!data.error) {
  //       dispatch(setStep(1))
  //     }
  //   })
  // }

  const handleFormSubmit = (data) => {
    const formData = new FormData()
    const userId = userData.id || userData.pk

    formData.append('id', userId)

    const formattedBirthDate = data.birth_date
      ? moment(data.birth_date).format('YYYY-MM-DD')
      : userData.profile.birth_date

    const profile = {
      ...userData.profile,
      ...data,
      birth_date: formattedBirthDate,
    }

    console.log(profile, 'Profile data to be submitted')
    Object.entries(profile).forEach(([key, value]) => {
      console.log(`Appending to FormData: ${key} = ${value}`)
      if (value === undefined || value === null || typeof value === 'object') return
      formData.append(key, value)
    })

    if (Array.isArray(profile.user_profile_languages)) {
      profile.user_profile_languages.forEach((lang, index) => {
        formData.append(`user_profile_languages[${index}][language]`, lang.language)
        formData.append(`user_profile_languages[${index}][level]`, lang.level)
      })
    }

    const file = data.profile_picture?.[0]
    if (file) {
      formData.append('profile_picture', file)
    }

    dispatch(updateRegistraionAbout(formData)).then((res) => {
      if (!res.error) {
        dispatch(setStep(1))
      }
    })
  }

  const handleOpenPublicView = () => {
    setIsPublicViewOpen(true)
  }

  const handleClosePublicView = () => {
    setIsPublicViewOpen(false)
  }

  const handleResetAvatar = () => {
    resetField('profile_picture')
  }

  return (
    <div className="font-Inter text-[#F67443] pb-16">
      <h3 className="text-2xl font-bold rounded-lg bg-[#FEEBDD] p-[20px]">Profile Settings</h3>

      {userData && profile && userData.profile && (
        <div className="mt-5 bg-[#FEEBDD] rounded-lg py-[80px]">
          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="max-w-[464px] w-full mx-auto flex flex-col items-center"
          >
            <h4 className="text-sm font-semibold text-primary-100">Your Profile Picture</h4>

            <div className="mt-3 flex flex-col items-center justify-center">
              <UploadAvatar
                gender={gender}
                picture={profile_picture}
                defaultAvatar={getAvatarUrl(profile?.profile_picture)}
                onResetAvatar={handleResetAvatar}
              >
                <input className="sr-only" type="file" {...register('profile_picture')} />
              </UploadAvatar>

              <label className="text-sm font-medium mt-3 flex items-center cursor-pointer">
                <Checkbox />

                <div className="flex flex-col items-start justify-start ml-[4px]">
                  <p className="text-sm font-normal leading-[20px] text-primary-100">
                    Automatically find my face and crop the photo.
                  </p>

                  <p className="text-xs font-normal leading-[16px] text-[#F79009]">
                    Note: max. size can’t be more than 2 Mb (JPG, PNG, and GIF formats)
                  </p>
                </div>
              </label>
            </div>

            <div className="mt-[24px] gap-y-[24px] w-full flex flex-col">
              <div className="w-full flex flex-col">
                <label htmlFor="first_name" className="text-sm font-semibold mb-[4px]">
                  First Name
                </label>

                {isTutor ? (
                  <input
                    id="first_name"
                    value={profile.first_name}
                    className="w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] text-[#f67443b3] text-base font-normal leading-[24px] placeholder:text-[#F6744388]"
                    disabled
                    readOnly
                  />
                ) : (
                  <>
                    <input
                      id="first_name"
                      placeholder="Enter your First Name"
                      className="outline-none w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] border-[#FFCA84] border-[1px] text-primary-100 text-base font-normal leading-[24px] placeholder:text-[#F6744388]"
                      {...register('first_name')}
                    />

                    {errors.first_name && (
                      <ErrorMessageWithExclamation text={errors.first_name.message} />
                    )}
                  </>
                )}
              </div>

              <div className="w-full flex flex-col">
                <label htmlFor="last_name" className="text-sm font-semibold mb-[4px]">
                  Last Name
                </label>

                {isTutor ? (
                  <input
                    id="last_name"
                    value={profile.last_name}
                    className="w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] text-[#f67443b3] text-base font-normal leading-[24px] placeholder:text-[#F6744388]"
                    disabled
                    readOnly
                  />
                ) : (
                  <>
                    <input
                      id="last_name"
                      placeholder="Enter your Last Name"
                      className="outline-none w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] border-[#FFCA84] border-[1px] text-primary-100 text-base font-normal leading-[24px] placeholder:text-[#F6744388]"
                      {...register('last_name')}
                    />

                    {errors.last_name && (
                      <ErrorMessageWithExclamation text={errors.last_name.message} />
                    )}
                  </>
                )}
              </div>

              <div className="w-full flex flex-col">
                <label htmlFor="nick-name" className="text-sm font-semibold mb-[4px]">
                  Nick Name
                </label>

                {isTutor ? (
                  <input
                    id="nick-name"
                    placeholder="Enter your Nick Name"
                    value={profile.username}
                    className="w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] text-[#f67443b3] text-base font-normal leading-[24px] placeholder:text-[#F6744388]"
                    readOnly
                    disabled
                  />
                ) : (
                  <>
                    <input
                      id="nick-name"
                      placeholder="Enter your Nick Name"
                      className="outline-none w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] border-[#FFCA84] border-[1px] text-primary-100 text-base font-normal leading-[24px] placeholder:text-[#F6744388]"
                      {...register('username')}
                    />

                    {errors.username && (
                      <ErrorMessageWithExclamation text={errors.username.message} />
                    )}
                  </>
                )}
              </div>

              <div className="w-full flex flex-col">
                <span className="text-sm font-semibold mb-[4px]">Gender</span>

                {isTutor ? (
                  <input
                    placeholder="Gender"
                    value={
                      genders.find((gender) => userData.profile.gender === gender.value)?.label ||
                      ''
                    }
                    className="w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] text-[#f67443b3] text-base font-normal leading-[24px] placeholder:text-[#F6744388]"
                    readOnly
                    disabled
                  />
                ) : (
                  <>
                    <Controller
                      control={control}
                      name="gender"
                      render={({ field: { value, onChange } }) => {
                        return (
                          <DropDown
                            placeholder="Gender"
                            menuItems={genders}
                            value={value}
                            setValue={onChange}
                            className="!h-[44px]"
                          />
                        )
                      }}
                    />

                    {errors.gender && <ErrorMessageWithExclamation text={errors.gender.message} />}
                  </>
                )}
              </div>

              <div className="w-full flex flex-col">
                <label htmlFor="date-of-birth" className="text-sm font-semibold mb-[4px]">
                  Date of Birth
                </label>

                {isTutor ? (
                  <div className="w-full relative flex items-center">
                    <input
                      id="date-of-birth"
                      value={
                        userData.profile.birth_date
                          ? userData.profile.birth_date.replaceAll('-', ' / ')
                          : ''
                      }
                      className="w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] text-[#f67443b3] text-base font-normal leading-[24px] placeholder:text-[#F6744388]"
                      disabled
                      readOnly
                    />

                    <img
                      src="/assets/icon/date-of-birth-calendar.svg"
                      alt="Calendar"
                      className="absolute w-[24px] h-[24px] right-[14px] opacity-70"
                    />
                  </div>
                ) : (
                  <>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <Controller
                        control={control}
                        name="birth_date"
                        render={({ field: { value, onChange } }) => {
                          return (
                            <DatePicker
                              minDate={moment().subtract(150, 'years')}
                              maxDate={moment().subtract(role === TUTOR_ROLE ? 18 : 16, 'years')}
                              value={value}
                              onChange={onChange}
                              className="custom-date-picker bg-white !w-full"
                              slotProps={{
                                textField: {
                                  readOnly: false,
                                  placeholder: 'YYYY/MM/DD',
                                },
                              }}
                            />
                          )
                        }}
                      />
                    </LocalizationProvider>

                    {errors.birth_date && (
                      <ErrorMessageWithExclamation text={errors.birth_date.message} />
                    )}
                  </>
                )}
              </div>

              <div className="w-full flex flex-col">
                <label htmlFor="email" className="text-sm font-semibold mb-[4px]">
                  Email
                </label>

                <input
                  id="email"
                  type="email"
                  value={userData.email}
                  placeholder="Email"
                  className="w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] text-[#f67443b3] text-base font-normal leading-[24px] placeholder:text-[#F6744388]"
                  disabled
                  readOnly
                />
              </div>

              <div className="w-full flex flex-col">
                <label htmlFor="phone-number" className="text-sm font-semibold mb-[4px]">
                  Phone Number
                </label>

                <div className="flex items-center">
                  <div className="max-w-[135px] w-full text-primary-100 !mr-2">
                    <Controller
                      control={control}
                      name="country_code"
                      render={({ field: { value, onChange } }) => {
                        return (
                          <DropDown
                            className="!h-[44px]"
                            value={value}
                            setValue={onChange}
                            menuItems={formattedCountryCodes}
                          />
                        )
                      }}
                    />
                  </div>

                  <Input
                    type="tel"
                    className="w-full h-[44px] py-0"
                    wrapperClassName="flex-1"
                    placeholder="Phone Number"
                    {...register('phone_number_part')}
                  />
                </div>

                {(errors.country_code || errors.phone_number_part) && (
                  <ErrorMessageWithExclamation
                    text={errors.country_code?.message || errors.phone_number_part.message}
                  />
                )}
              </div>
            </div>

            <Button
              type="submit"
              className="mt-[20px] w-full !text-sm !font-semibold"
              isLoading={isLoading}
            >
              Save Changes
            </Button>

            <button
              type="button"
              className="text-base font-normal mt-[20px] hover:text-[#f67433] hover:underline"
              onClick={handleOpenPublicView}
            >
              See Public View
            </button>
          </form>
        </div>
      )}

      <ProfilePublicView open={isPublicViewOpen} onClose={handleClosePublicView} />
    </div>
  )
}

export default Profile
